import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, UserProfile, clearSupabaseData, forceLogout, getCachedAuthUser, clearApiCache } from '../lib/supabaseClient';
import { useToast } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setUser as setGAUser } from '../services/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isSubscriber: boolean;
  subscriptionPlan: 'free' | 'pro' | 'pro_yearly' | null;
  passwordSet: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithOtp: (email: string, captchaToken?: string, shouldCreateUser?: boolean) => Promise<void>;
  checkPasswordSet: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<'free' | 'pro' | 'pro_yearly' | null>(null);
  const [passwordSet, setPasswordSet] = useState(true); // Default to true to avoid unwanted redirects
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user profile and subscription status
  const fetchUserProfile = async (userId: string) => {
    if (!userId) return;
    
    try {
      console.log('Fetching user profile for:', userId);
      
      // Fetch user profile from the public.users table to get role/subscription info
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (userError) {
        console.error('Error fetching user data:', userError);
        // Continue with basic profile fetch anyway
      } else if (userData) {
        console.log('Successfully retrieved user data with role:', userData.role);
        // Check if user has an active subscription based on role
        const hasSubscription = userData.role === 'pro' || userData.role === 'pro_yearly';
        setIsSubscriber(hasSubscription);
        setSubscriptionPlan(userData.role || 'free');
        
        // Set passwordSet state based on the userData
        setPasswordSet(userData.password_set === true);
        console.log('Password set status:', userData.password_set);

        // If the password is not set, redirect to the create-password page
        // But only if we're not already on that page
        if (userData.password_set === false && location.pathname !== '/create-password') {
          console.log('Password not set, redirecting to create-password page');
          navigate('/create-password');
        }
      }
      
      // Use the cached auth user endpoint instead of direct API call
      try {
        const authUser = await getCachedAuthUser();
        if (authUser) {
          console.log('Successfully retrieved user profile from cached endpoint');
          setUserProfile({
            ...authUser as UserProfile,
            password_set: userData?.password_set
          });
          return;
        }
      } catch (cacheError) {
        console.error('Error fetching from cached endpoint:', cacheError);
        // Fall back to standard method
      }
      
      // Continue with standard profile fetch as fallback
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        
        // If no profile exists yet, create a basic profile
        if (user && error.code === 'PGRST116') {
          console.log('No profile exists, creating basic profile');
          const basicProfile: UserProfile = {
            id: userId,
            email: user.email || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            password_set: false // Default to false for new users
          };
          
          const { error: insertError } = await supabase
            .from('profiles')
            .insert(basicProfile);
            
          if (insertError) {
            console.error('Error creating user profile:', insertError);
            return;
          }
          
          setUserProfile(basicProfile);
          return;
        }
        
        return;
      }

      console.log('Successfully retrieved user profile');
      setUserProfile({
        ...(user as UserProfile),
        password_set: userData?.password_set
      });
    } catch (e) {
      console.error('Exception fetching user profile:', e);
    }
  };

  // Add a function to check if password is set
  const checkPasswordSet = () => {
    return passwordSet;
  };

  // Initialize auth state
  useEffect(() => {
    // Get the current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
        // Track user in Google Analytics
        setGAUser(session.user.id);
      }
      
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log(`Auth state changed: ${event}`);
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        if (newSession?.user) {
          // Only fetch profile on specific auth events
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
            fetchUserProfile(newSession.user.id);
            // Track user in Google Analytics
            setGAUser(newSession.user.id);
          }
        } else {
          setUserProfile(null);
          setIsSubscriber(false);
          setSubscriptionPlan(null);
          setPasswordSet(true); // Reset to default
          // Clear user from Google Analytics
          setGAUser('anonymous');
        }
        
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  // Sign in with email and password
  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      // Create sign in options with captcha token if provided
      const options = captchaToken ? {
        options: {
          captchaToken
        }
      } : {};
      
      // Log what we're sending (without the password)
      console.log('SignIn options:', JSON.stringify({
        ...options,
        email
      }));
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        ...options
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Error during sign out:', error);
        // Try with force logout as fallback
        await forceLogout();
      }
      
      // Reset subscription state
      setIsSubscriber(false);
      setSubscriptionPlan(null);
      setPasswordSet(true); // Reset to default
      
      // Clear the API cache when signing out
      clearApiCache();
      
      // Show logout confirmation
      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error('Exception during sign out:', e);
      clearSupabaseData();
    }
  };

  // Sign in with OTP (magic link or email code)
  const signInWithOtp = async (email: string, captchaToken?: string, shouldCreateUser?: boolean) => {
    const options = captchaToken ? { 
      captchaToken,
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      shouldCreateUser
    } : {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      shouldCreateUser
    };
    
    const { error } = await supabase.auth.signInWithOtp({ 
      email,
      options
    });
    
    if (error) throw error;
  };

  const value = {
    user,
    session,
    userProfile,
    loading,
    isSubscriber,
    subscriptionPlan,
    passwordSet,
    signIn,
    signUp,
    signOut,
    signInWithOtp,
    checkPasswordSet
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 